/**
 * The front-end side of the extensions: every `extensions/<name>/js/extension.ts`, kept while the
 * server lists the extension in `window.eflib`. See lib/docs/extensions/writing-an-extension.md.
 *
 *     <efExtensionTabs name="admin.settings" />
 *     routes: [...appRoutes, ...extensionRoutes()]
 *     menu: (user, context) => [..., ...extensionMenu('admin', user, context)]
 */
import { merge } from 'lodash-es';
import type { App } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

import { hasExtension } from '@lib/utils/eflib';
import type { SidebarItemType } from '@lib/stores/app-sidebar-menu';
import type { AuthUser } from '@lib/types';
import type { useAppContextStore } from '@lib/plugins/layouts/context';

import type { AppContributions, ExtensionComponent, ExtensionManifest, ExtensionSlotName, ExtensionSlots, LocaleTree, SlotContribution } from './define';

export * from './define';

/** How an app narrows a slot: contributions it leaves out, and the ids that come first, in order. */
export type SlotOptions = { hide?: string[]; order?: string[] };

/** The app's part, from the lib's plugin options: what it adds to the slots, and how it narrows them. */
export type ExtensionOptions = {
	slots?: { [S in ExtensionSlotName]?: SlotOptions };
	contributions?: AppContributions;
};

const modules = import.meta.glob<{ default: ExtensionManifest }>('../../extensions/*/js/extension.ts', { eager: true });

/** Every manifest, on or off, by name. */
const manifests: ExtensionManifest[] = Object.values(modules)
	.map((module) => module.default)
	.sort((a, b) => a.name.localeCompare(b.name));

let options: ExtensionOptions = {};

/** Sets the app's contributions and slot options, from the lib's plugin options. */
export function configureExtensions(value: ExtensionOptions | undefined): void {
	options = value ?? {};
}

/** Every manifest, also of the extensions that are off - for their texts. */
export function allExtensionManifests(): ExtensionManifest[] {
	return manifests;
}

/** The manifests of the extensions that are on. */
export function extensionManifests(): ExtensionManifest[] {
	return manifests.filter((manifest) => hasExtension(manifest.name));
}

/** The texts of every extension, to merge under the lib's own. */
export function extensionLocale(): { de: LocaleTree; en: LocaleTree } {
	return manifests.reduce<{ de: LocaleTree; en: LocaleTree }>(
		(locale, manifest) => merge(locale, { de: manifest.locale?.de ?? {}, en: manifest.locale?.en ?? {} }),
		{ de: {}, en: {} },
	);
}

/** Runs the setup of every extension that is on. */
export function setupExtensions(app: App): void {
	for (const manifest of extensionManifests()) manifest.setup?.(app);
}

/** The routes of the extensions that are on, for the app to spread into its dashboard layout. */
export function extensionRoutes(): RouteRecordRaw[] {
	return extensionManifests().flatMap((manifest) => manifest.routes ?? []);
}

/** The widget types of the extensions that are on, for efDashboard's registry. */
export function extensionWidgets(): Record<string, ExtensionComponent> {
	return Object.assign({}, ...extensionManifests().map((manifest) => manifest.widgets ?? {}));
}

/** The sidebar entries of a section, for the app's menu builder to place. */
export function extensionMenu(
	section: string,
	user: AuthUser,
	context: ReturnType<typeof useAppContextStore>,
): SidebarItemType[] {
	return extensionManifests().flatMap((manifest) => manifest.sidebar?.[section]?.(user, context) ?? []);
}

/**
 * What the extensions that are on and the app put into a slot: for these props, while their `when`
 * holds, without what the app hides, the app's order first and then by `order`. Permissions are
 * left to the component that renders them, which has the user. Contributions that replace a tab
 * of the page come along; efExtensionTabs leaves them to efSlotTab.
 */
export function slotContributions<S extends ExtensionSlotName>(
	slot: S,
	props: ExtensionSlots[S],
): Array<SlotContribution<ExtensionSlots[S]>> {
	const { hide = [], order = [] } = options.slots?.[slot] ?? {};
	const context = (props as { context?: string }).context;
	const rank = (contribution: SlotContribution<ExtensionSlots[S]>): number => {
		const position = order.indexOf(contribution.id);
		return position >= 0 ? position - order.length : (contribution.order ?? 100);
	};

	const sources: Array<Pick<ExtensionManifest, 'slots'>> = [...extensionManifests(), options.contributions ?? {}];

	return sources
		.flatMap((source) => (source.slots?.[slot] ?? []) as Array<SlotContribution<ExtensionSlots[S]>>)
		.filter((contribution) => !hide.includes(contribution.id))
		.filter((contribution) => !contribution.contexts || (context !== undefined && contribution.contexts.includes(context)))
		.filter((contribution) => contribution.when?.(props) ?? true)
		.sort((a, b) => rank(a) - rank(b));
}
